import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["interactive", "onClick", "onKeyDown"];
import PropTypes from 'prop-types';
import React, { forwardRef, useRef } from 'react';
import { CARD_TYPES, KEY_CODES } from '../../constants';
import { StyledCard } from './Card.styles';

/**
 * @since 4.1.0
 */

export var Card = /*#__PURE__*/forwardRef(function (_ref, forwardedRef) {
  var interactive = _ref.interactive,
    onClick = _ref.onClick,
    onKeyDown = _ref.onKeyDown,
    rest = _objectWithoutProperties(_ref, _excluded);
  var defaultInnerRef = useRef();
  var innerRef = forwardedRef || defaultInnerRef;

  /**
   * if the card is interactive and an onClick function was provided,
   *   execute that function if the user presses the Enter or Space key
   *   while the card has focus.
   */
  var keyDownCallback = function keyDownCallback(e) {
    // Only execute if the card itself was clicked, not one of its children.
    if (e.target !== innerRef.current) {
      return;
    }
    if (typeof onKeyDown === 'function') {
      onKeyDown(e);
    }
    if ((e.keyCode === KEY_CODES.ENTER || e.keyCode === KEY_CODES.SPACE) && typeof onClick === 'function') {
      onClick(e);
      e.preventDefault();
    }
  };
  var cardProps = Object.assign({
    interactive: interactive,
    onClick: onClick,
    onKeyDown: keyDownCallback,
    role: interactive ? 'button' : undefined,
    tabIndex: interactive ? 0 : undefined,
    ref: innerRef
  }, rest);
  return /*#__PURE__*/React.createElement(StyledCard, cardProps);
});
Card.displayName = 'Card';
Card.propTypes = process.env.NODE_ENV !== "production" ? {
  children: PropTypes.node,
  /**
   * This prop allows you to specify that a card should have no border.
   */
  appearance: PropTypes.oneOf(['default', 'borderless']),
  /** A `primary` card has a highlighted border, where as a `secondary` card does not. */
  cardType: PropTypes.oneOf(Object.values(CARD_TYPES)),
  /**
   * This integer determines whether we use a shadow/elevation (1) or no elevation (0).
   */
  cardElevation: PropTypes.oneOf([0, 1]),
  /**
   * Change the direction of the accent border of `"primary"` cards.
   */
  cardDirection: PropTypes.oneOf(['top', 'bottom', 'left', 'right']),
  /**
   * Adds interactive styling to the card (hover, cursor pointer, etc).
   */
  interactive: PropTypes.bool,
  /**
   * Callback function that's called whenever you click on the card.
   *   Be sure to add `stopPropagation` to any clickable child elements
   *   (ex: buttons or links). If an onClick function is provided, it
   *   will also be executed when the user presses the space or enter key
   *   while the card is focused.
   */
  onClick: PropTypes.func,
  /**
   * Used for accessibility. If the Card's `interactive` prop is set,
   *   the `role` is set to `"button"` by default.
   */
  role: PropTypes.string
} : {};
Card.defaultProps = {
  appearance: 'default',
  cardType: CARD_TYPES.primary,
  cardElevation: 1,
  cardDirection: 'bottom'
};